import React, { useEffect, useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber, formatMoney } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

import { H3, Page, NamedLink, NamedRedirect, LayoutSingleColumn, H4 } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  fetchListingsRentalPlaces,
  fetchOwnBrandProfileListings,
  fetchMonthlyTimeSlotsForDisplayListing,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import RentalsSection from './RentalsSection/RentalsSection';

import { getIsShopsAndBrands } from '../../util/userHelpers.js';
import StoreDetailsSectionListingPage from './StoreDetailsSectionListingPage/StoreDetailsSectionListingPage';
import { usePlatformClassName } from '../../hooks/usePlatform';
import { languagesHelper as language } from '../../helpers/languages';
import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

const getPlatform = () => {
  const platform = navigator.platform.toLowerCase();
  if (platform.includes('mac')) return 'mac';
  if (platform.includes('win')) return 'windows';
  return 'other';
};

export const ListingPageComponent = props => {
  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showListingsRentalPlacesError,
    fetchListingsRentalPlacesInProgress,
    listingsRentalPlaces,
    onFetchListingsRentalPlaces,
    onFetchOwnBrandProfileListings,
    ownBrandProfileListings,
    showOwnBrandProfileListingsError,
    showOwnBrandProfileListingsInProgress,
    onFetchMonthlyTimeSlotsForDisplayListing,
  } = props;

  let currentLocale = language.get();
  const isDe = currentLocale === "de";

  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [activeRental, setActiveRental] = useState();
  const [currentTx, setCurrentTx] = useState(null);

  const platformClassName = usePlatformClassName(css.contentWrapperForProductLayout, {
    mac: css.isMac,
    windows: css.isWindows,
  });

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const activeId = query.get('active');

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const rentalDisplayId = rawParams?.variant ? new UUID(rawParams.variant) : '';
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const isShopsAndBrands = getIsShopsAndBrands(currentUser);

  // useEffect(() => {
  //   if (!listingsRentalPlaces?.length) {
  //     onFetchListingsRentalPlaces(listingId, config);
  //   }

  //   if (!ownBrandProfileListings?.length && isShopsAndBrands) {
  //     onFetchOwnBrandProfileListings(config);
  //   }
  // }, []);

  // The gallery doesn't account for the scrollbar on Windows.
  // This causes a horizontal scrollbar to appear.
  // We apply different styles depending on the operating system type.

  useEffect(() => {
    if (listingsRentalPlaces && activeId) {
      const activeRentalListing = listingsRentalPlaces.find(l => l?.id?.uuid === activeId);
      setActiveRental(activeRentalListing);
    }
  }, [listingsRentalPlaces, activeId]);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  useEffect(() => {
    const delayFetchListings = () => {
      onFetchListingsRentalPlaces(listingId, config);
    };

    const timeoutId = setTimeout(delayFetchListings, 1000); // Delay of 1 second (1000 ms)

    if (isShopsAndBrands) {
      onFetchOwnBrandProfileListings(config);
    }

    return () => clearTimeout(timeoutId); // Clear timeout if the component unmounts
  }, [isShopsAndBrands, location.search]);

  const brandProfileListingPendingApproval = ownBrandProfileListings?.find(
    l => l.attributes?.publicData?.isApproved === false
  );
  const hasBrandProfileListing = ownBrandProfileListings?.find(
    l => l.attributes?.publicData?.isApproved === true
  );

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (
    showListingError ||
    showListingsRentalPlacesError ||
    showOwnBrandProfileListingsError
  ) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (
    !currentListing.id ||
    fetchListingsRentalPlacesInProgress ||
    showOwnBrandProfileListingsInProgress ||
    !Array.isArray(listingsRentalPlaces)
  ) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (
    currentListing?.id &&
    !fetchListingsRentalPlacesInProgress &&
    Array.isArray(listingsRentalPlaces) &&
    !listingsRentalPlaces?.length
  ) {
    return <NamedRedirect name="LandingPage" />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(currentListing?.attributes?.title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias;
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? ('booking' ? isPurchase : 'purchase') : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const languageProvider = ensuredAuthor?.attributes?.profile?.publicData?.language;
  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });

  const discountPrice = (() => {
    const price = lineItems?.find(item => item.code === "line-item/discount")?.unitPrice;
    if (price == null) return null;

    return formatMoney(intl, price, false)
      .replace(/[-€\s]/g, "");
  })();

  const emailData = {
    brandName: ownBrandProfileListings?.[0]?.attributes?.title,
    shopName: currentListing?.attributes?.title,
  };

  if (!!discountPrice) {
    emailData.discountPrice = discountPrice.replace(",", ".");
  }

  const customerTotal = currentTx?.attributes?.payinTotal?.amount / 100;
  const providerTotal = currentTx?.attributes?.payoutTotal?.amount / 100;

  const calcNetVat = total => {
    const net = (total / 1.19);
    const vat = (total - net);
    return {
      net: net.toFixed(2),
      vat: vat.toFixed(2)
    };
  };

  if (customerTotal != null && providerTotal != null) {
    const { net: customerNet, vat: customerVat } = calcNetVat(customerTotal);
    const { net: providerNet, vat: providerVat } = calcNetVat(providerTotal);

    emailData.customerNet = customerNet;
    emailData.customerVat = customerVat;
    emailData.providerNet = providerNet;
    emailData.providerVat = providerVat;
  }

  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    activeRental,
    emailData,
    languageProvider
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = activeRental.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
      price: intl.formatNumber(convertMoneyToNumber(price), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      priceCurrency: price.currency,
    }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const locationTypes = currentListing?.attributes?.publicData?.locationType;
  const locationTypeOptions =
    listingFieldsConfig?.find(c => c.key === 'locationType').associations || {};
  const displayTypeOptions =
    listingFieldsConfig?.find(c => c.key === 'rentalSpaceDisplayTypes').associations || {};

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={platformClassName}>
          {currentListing?.id ? (
            <ActionBarMaybe
              className={css.actionBarForProductLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
                kind: 'shop',
              }}
            />
          ) : null}

          <SectionGallery
            platformClassName={platformClassName}
            listing={currentListing}
            variantPrefix={config.layout.shopImage.variantPrefix}
            config={config}
            intl={intl}
            onManageDisableScrolling={onManageDisableScrolling}
          />

          <div className={css.mainColumnForProductLayout}>
            {currentListing?.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
              />
            ) : null}

            <h1 className={css.mainTitle}>
              <span>{title}</span>
            </h1>
            {/*<div>{locationTypes?.map(l => locationTypeOptions?.[l])}</div>*/}
            <div className={css.locations}>
              <label className={css.mainLabel}><FormattedMessage id="ListingPage.location" /></label>
              <div className={css.container}>
                {locationTypes?.map((item, index) => {
                  return (
                    <React.Fragment key={index}>
                      <div className={css.item}>{isDe ? locationTypeOptions?.[item]?.labelDe : locationTypeOptions?.[item]?.label}</div>
                      {index < locationTypes.length - 1 && <span className={css.dot}>•</span>}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>

            <div className={css.heading} style={{ marginBottom: 20 }}>
              <H4 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.ourDisplays" values={{ count: listingsRentalPlaces?.length || 0 }} />
              </H4>
            </div>
          </div>
        </div>
        {listingsRentalPlaces?.length > 0 && (
          <RentalsSection
            config={config}
            measureUnit={currentListing?.attributes?.publicData?.measureUnit === 'm2' ? 'm' : 'ft'}
            configLayout={config?.layout?.displayImage}
            rentals={listingsRentalPlaces}
            currentListing={currentListing}
            rentalDisplayId={rentalDisplayId}
            variantPrefix={config?.layout?.displayImage?.variantPrefix}
            intl={intl}
            activeId={activeId}
            onFetchMonthlyTimeSlotsForDisplayListing={onFetchMonthlyTimeSlotsForDisplayListing}
            // ORDER PANEL PROPS
            isOwnListing={isOwnListing}
            onSubmit={handleOrderSubmit}
            authorLink={
              <NamedLink
                className={css.authorNameLink}
                name="ListingPage"
                params={params}
                to={{ hash: '#author' }}
              >
                {authorDisplayName}
              </NamedLink>
            }
            title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
            titleDesktop={
              <H4 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H4>
            }
            payoutDetailsWarning={payoutDetailsWarning}
            author={ensuredAuthor}
            onManageDisableScrolling={onManageDisableScrolling}
            onContactUser={onContactUser}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            validListingTypes={config.listing.listingTypes}
            marketplaceCurrency={config.currency}
            dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
            marketplaceName={config.marketplaceName}
            activeRental={activeRental}
            setActiveRental={setActiveRental}
            brandProfileListingPendingApproval={brandProfileListingPendingApproval}
            hasBrandProfileListing={hasBrandProfileListing}
            currentUser={currentUser}
            displayTypeOptions={displayTypeOptions}
            detailsSection={
              <StoreDetailsSectionListingPage
                rootClassName={css.storeDetailSectionRoot}
                currentListing={currentListing}
                intl={intl}
                marketplaceCurrency={config.currency}
                listingPage={true}
                listingFieldsConfig={listingFieldsConfig}
              />
            }
            brand={ownBrandProfileListings?.[0]}
            isDe={isDe}
            setCurrentTx={setCurrentTx}
          />
        )}
        <SectionMapMaybe
          geolocation={currentListing?.attributes.geolocation}
          publicData={currentListing?.attributes.publicData}
          listingId={currentListing?.id}
          mapsConfig={config.maps}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    listingsRentalPlaces,
    showListingsRentalPlacesError,
    fetchListingsRentalPlacesInProgress,
    ownBrandProfileListings,
    showOwnBrandProfileListingsError,
    showOwnBrandProfileListingsInProgress,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    listingsRentalPlaces,
    showListingsRentalPlacesError,
    fetchListingsRentalPlacesInProgress,
    ownBrandProfileListings,
    showOwnBrandProfileListingsError,
    showOwnBrandProfileListingsInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onFetchMonthlyTimeSlotsForDisplayListing: listing =>
    dispatch(fetchMonthlyTimeSlotsForDisplayListing(listing)),
  onFetchListingsRentalPlaces: (storeId, config) =>
    dispatch(fetchListingsRentalPlaces(storeId, config)),
  onFetchOwnBrandProfileListings: config => dispatch(fetchOwnBrandProfileListings(config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
